import { mark_material_rendered, is_material_rendered } from './Material.js';
export class MaterialFreezingManager {
    constructor(scene, _freezing_allowed) {
        this.scene = scene;
        this._freezing_allowed = _freezing_allowed;
        this._all_materials_dirty = false;
        this._dirty = new Set();
        this._unfrozen = new Set();
        this._on_new_light_added = null;
        this._on_light_removed = null;
        this._on_geometry_added = null;
        this._on_geometry_removed = null;
        this._on_mesh_added = null;
        this._on_texture_added = null;
        this._on_before_render = null;
        this._on_material_added = null;
        this._on_material_removed = null;
        this._material_to_on_on_material_bound = new Map();
        this._enabled = true;
        this.mark_materials_dirty = () => {
            this._all_materials_dirty = true;
        };
        if (this._enabled) {
            this.add_observables();
        }
    }
    add_observables() {
        this._on_new_light_added = this.scene.onNewLightAddedObservable.add(this.mark_materials_dirty);
        this._on_light_removed = this.scene.onLightRemovedObservable.add(this.mark_materials_dirty);
        // @Martin Figure out if we can remove any of the following callbacks. Unfreezing everything is expensive.
        this._on_geometry_added = this.scene.onNewGeometryAddedObservable.add(this.mark_materials_dirty);
        this._on_geometry_removed = this.scene.onGeometryRemovedObservable.add(this.mark_materials_dirty);
        this._on_mesh_added = this.scene.onNewMeshAddedObservable.add(this.mark_materials_dirty);
        this._on_texture_added = this.scene.onNewTextureAddedObservable.add(this.mark_materials_dirty);
        this._on_material_added = this.scene.onNewMaterialAddedObservable.add((material) => {
            this.on_new_material_added(material);
        });
        this._on_material_removed = this.scene.onMaterialRemovedObservable.add((material) => {
            this._material_to_on_on_material_bound.delete(material);
        });
        this._on_before_render = this.scene.onBeforeRenderObservable.add(() => {
            this.process_material_freezing();
        });
    }
    on_new_material_added(material) {
        material.onCompiled = () => {
            if (material.isFrozen) {
                this.set_material_dirty(material);
            }
        };
        const on_material_bound = material.onBindObservable.add(() => {
            mark_material_rendered(material);
        });
        this._material_to_on_on_material_bound.set(material, on_material_bound);
        this.set_material_dirty(material);
    }
    set_all_materials_dirty() {
        this._all_materials_dirty = true;
    }
    set_material_dirty(material) {
        if (!this._all_materials_dirty) {
            this._dirty.add(material);
        }
    }
    process_material_freezing() {
        const disable_freezing = this._freezing_allowed();
        if (this._all_materials_dirty) {
            // When all materials are dirty, we unfreeze all frozen materials and clear the dirty set.
            for (const mtl of this.scene.materials) {
                this.unfreeze(mtl);
            }
            this._all_materials_dirty = false;
            this._dirty.clear();
            // We can return early here since we know that we can't freeze any materials yet.
            return;
        }
        for (const material of this._dirty) {
            this.unfreeze(material);
        }
        this._dirty.clear();
        if (!disable_freezing) {
            for (const mtl of this._unfrozen) {
                if (is_material_rendered(mtl)) {
                    mtl.freeze();
                    this._unfrozen.delete(mtl);
                }
            }
        }
    }
    unfreeze(mtl) {
        if (mtl.isFrozen) {
            mtl.unfreeze();
        }
        mark_material_rendered(mtl, false);
        this._unfrozen.add(mtl);
    }
    get enabled() {
        return this._enabled;
    }
    set enabled(enable) {
        if (enable && !this._enabled) {
            this.add_observables();
        }
        else if (!enable && this._enabled) {
            this.remove_observables();
        }
        this._enabled = enable;
    }
    remove_observables() {
        if (this._on_new_light_added) {
            this.scene.onNewLightAddedObservable.remove(this._on_new_light_added);
        }
        if (this._on_new_light_added) {
            this.scene.onLightRemovedObservable.remove(this._on_new_light_added);
        }
        // @Martin Figure out if we can remove any of the following callbacks. Unfreezing everything is expensive.
        if (this._on_geometry_added) {
            this.scene.onNewGeometryAddedObservable.remove(this._on_geometry_added);
        }
        if (this._on_geometry_removed) {
            this.scene.onGeometryRemovedObservable.remove(this._on_geometry_removed);
        }
        if (this._on_mesh_added) {
            this.scene.onNewMeshAddedObservable.remove(this._on_mesh_added);
        }
        if (this._on_texture_added) {
            this.scene.onNewTextureAddedObservable.remove(this._on_texture_added);
        }
        if (this._on_material_added) {
            this.scene.onNewMaterialAddedObservable.remove(this._on_material_added);
        }
        if (this._on_material_removed) {
            this.scene.onMaterialRemovedObservable.remove(this._on_material_removed);
        }
        if (this._on_before_render) {
            this.scene.onBeforeRenderObservable.remove(this._on_before_render);
        }
        this._material_to_on_on_material_bound.forEach((callback, material) => {
            material.onCompiled = null;
            material.onBindObservable.remove(callback);
        });
        this._freezing_allowed = () => true;
        this._all_materials_dirty = true;
        this.process_material_freezing();
    }
}
//# sourceMappingURL=MaterialFreezingManager.js.map